import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventName } from '#~/analytics/constants/event-names.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';
import type { LinkingSurface } from './linking-surface.js';

/**
 * Embedded-device-flow analytics (ENG-3960 §A.5, item 5).
 *
 * The embedded (Iris-firmware) camera flow reuses the shared Add Device wizard
 * step set (§1.6) rather than a parallel UI, so per rule investigate-before-adding
 * this tracker REUSES the existing canonical wizard event names — it never mints
 * embedded-only event names. The embedded dimension is carried purely as the
 * `is_embedded: true` + `linking_surface` cohort props on those same events, which
 * is what lets A1 reconcile the embedded funnel against the generic one.
 *
 * Mirrored wizard step → event map (the §1.6 set, fired today in the add/ footers
 * and the discovery table):
 *   trackDiscovered          → device_click_discovered
 *   trackSelectBrand         → device_select_brand
 *   trackContinueFromBrand   → device_continue_from_brand
 *   trackSelectModel         → device_select_model
 *   trackContinueFromControl → device_continue_from_control
 *   trackContinueToVideo     → device_continue_to_video
 *   trackContinueFromVideo   → device_continue_from_video
 *   trackAdded               → device_add
 */

/**
 * Canonical device-cohort props (ENG-3960 §E12) forwarded onto every embedded
 * step event. Only canonical keys are accepted — the off-convention aliases
 * (`brand`/`model`/…) are rejected by the runtime guard in track.ts, so callers
 * must enrich via resolveDeviceAnalyticsContext / resolveDeviceAnalyticsCatalogContext.
 */
export interface EmbeddedDeviceStepProperties extends EventProperties {
  device_id?: string;
  device_model_id?: string;
  model_name?: string;
  device_brand_id?: string;
  brand_name?: string;
  studio_id?: string;
  agent_id?: string;
}

export interface EmbeddedDeviceFlowTrackerOptions {
  /** Surface the flow is running on; stamped onto every emitted step event. */
  linking_surface: LinkingSurface;
}

/**
 * Create an embedded-device-flow tracker bound to a single flow instance.
 *
 * Stamps `is_embedded: true` + `linking_surface` onto every step event and guards
 * against double-firing within the flow: each (event, device_id) pair fires at
 * most once, so a React re-render or a double-click on "Continue" cannot inflate
 * the funnel. The guard is keyed per device so adding two embedded cameras in one
 * session both register; call {@link reset} when a fresh flow starts.
 *
 * Per rule: ai-code-requires-tests — firing + no-double-fire + per-device keying
 * are all covered by capture-spy assertions in embedded-device-flow.test.ts.
 */
export const createEmbeddedDeviceFlowTracker = (
  track: AnalyticsTrackFn,
  { linking_surface }: EmbeddedDeviceFlowTrackerOptions
) => {
  // Per-instance once-guard. In-memory (not sessionStorage) because it is scoped
  // to one wizard flow instance — the React caller holds the tracker stable via
  // useMemo, so the guard lives exactly as long as the flow does.
  const fired = new Set<string>();

  const emit = (name: EventName, properties: EmbeddedDeviceStepProperties): void => {
    const key = `${name}:${properties.device_id ?? ''}`;

    if (fired.has(key)) {
      return;
    }

    fired.add(key);

    track(name, EVENT_CATEGORIES.DEVICE, {
      ...properties,
      is_embedded: true,
      linking_surface
    });
  };

  return {
    trackDiscovered: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_CLICK_DISCOVERED, properties),

    trackSelectBrand: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_SELECT_BRAND, properties),

    trackContinueFromBrand: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_CONTINUE_FROM_BRAND, properties),

    trackSelectModel: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_SELECT_MODEL, properties),

    trackContinueFromControl: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_CONTINUE_FROM_CONTROL, properties),

    trackContinueToVideo: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO, properties),

    trackContinueFromVideo: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_CONTINUE_FROM_VIDEO, properties),

    trackAdded: (properties: EmbeddedDeviceStepProperties = {}): void =>
      emit(EVENT_NAMES.DEVICE_ADD, properties),

    /** Re-arm the once-guard when a fresh flow starts (e.g. wizard reopened). */
    reset: (): void => {
      fired.clear();
    }
  };
};
